import type { SarahMessage, SarahSession } from '@/domain/sarah'
import type { SarahSessionRepository } from '@/repositories/SarahSessionRepository'

const STORAGE_KEY = 'cybertales.sarahSessions'

function readAll(): SarahSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as SarahSession[]) : []
  } catch {
    return []
  }
}

function writeAll(items: SarahSession[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // best-effort — Sarah's conversation history simply won't persist across reloads.
  }
}

// Zero-cost implementation of SarahSessionRepository (Document 11 §4, P3).
// One session per userId, exactly like every other Local*Repository's
// per-user scoping (LocalProgressRepository, LocalResultsRepository, etc.).
// CosmosSarahSessionRepository (P5) would implement the same interface
// against Cosmos DB with a real TTL/retention policy.
export class LocalSarahSessionRepository implements SarahSessionRepository {
  async getSession(userId: string): Promise<SarahSession | null> {
    return readAll().find((s) => s.userId === userId) ?? null
  }

  async getOrCreateSession(userId: string, orgId: string | null): Promise<SarahSession> {
    const all = readAll()
    const existing = all.find((s) => s.userId === userId)
    if (existing) return existing

    const session: SarahSession = {
      id: crypto.randomUUID(),
      userId,
      orgId,
      createdAt: new Date().toISOString(),
      messages: [],
    }
    all.push(session)
    writeAll(all)
    return session
  }

  async appendMessage(userId: string, message: SarahMessage): Promise<SarahSession> {
    const all = readAll()
    const idx = all.findIndex((s) => s.userId === userId)
    if (idx < 0) {
      // Defensive fallback — should not happen if callers always
      // getOrCreateSession() first, but never silently drop a message.
      const session: SarahSession = {
        id: crypto.randomUUID(),
        userId,
        orgId: null,
        createdAt: new Date().toISOString(),
        messages: [message],
      }
      all.push(session)
      writeAll(all)
      return session
    }
    all[idx] = { ...all[idx], messages: [...all[idx].messages, message] }
    writeAll(all)
    return all[idx]
  }

  async clearSession(userId: string): Promise<void> {
    writeAll(readAll().filter((s) => s.userId !== userId))
  }
}
